import React from "react";
import {adminParent} from "../utils/adminNavigationContext";
import {Link, useLocation} from "react-router-dom";
import { connectionFailure } from "../utils/connectionErrors";
import { retryFailedRouteLoads } from "../utils/retryableLazy";

class RouteErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, resetKey: props.resetKey, retryable: false, retries: 0 };
  }

  static getDerivedStateFromProps(props, state) {
    return props.resetKey !== state.resetKey ? {hasError: false, resetKey: props.resetKey, retries: 0} : null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, retryable: connectionFailure(error) === "connection" };
  }

  componentDidMount() { window.addEventListener("online", this.reconnect); }
  componentWillUnmount() { window.removeEventListener("online", this.reconnect); }
  retry = () => {
    retryFailedRouteLoads();
    this.setState(state => ({hasError: false, retries: state.retries + 1}));
  };
  reconnect = () => {
    if (this.state.hasError && this.state.retryable && this.state.retries < 2) this.retry();
  };

  componentDidCatch(error, info) {
    console.error("Lightly route failed to render", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" className="min-h-screen bg-[#f7f3ea] px-6 py-16 text-slate-950">
          <div className="mx-auto max-w-2xl rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-600">
              Page recovery
            </p>
            {this.state.retryable && <button type="button" onClick={this.retry} className="mt-6 mr-3 rounded-full bg-blue-700 px-5 py-3 text-sm font-bold text-white">Retry loading</button>}
            <h1 className="mt-3 text-3xl font-black tracking-[-0.04em]">
              This page needs a quick refresh.
            </h1>
            <p className="mt-3 text-sm font-semibold leading-6 text-slate-600">
              The route failed while loading its data. Refresh the page, or use the navigation to continue.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-6 rounded-full bg-slate-950 px-5 py-3 text-sm font-black text-white"
            >
              Refresh page
            </button>
            {this.props.parent && <Link className="ml-3 inline-flex min-h-11 items-center text-indigo-700" to={this.props.parent.href}>Back to {this.props.parent.label}</Link>}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <RouteErrorBoundaryInner parent={adminParent(location.pathname)} resetKey={`${location.pathname}${location.search}`}>
      {children}
    </RouteErrorBoundaryInner>
  );
}

